import { cloneElement, forwardRef, isValidElement, useId } from 'react';
import { combineClassNames } from '../../../Utils/ClassnameUtils';

export interface GWPFieldProps extends React.ComponentPropsWithoutRef<'div'> {
	children: React.ReactNode;
	label?: React.ReactNode;
	hint?: React.ReactNode;
	error?: React.ReactNode;
	secondaryText?: React.ReactNode;
	htmlFor?: string;
	invalid?: boolean;
	required?: boolean;
	optional?: boolean;
	labelHidden?: boolean;
}

const GWPField = forwardRef<HTMLDivElement, GWPFieldProps>((props, ref) => {
	const {
		children,
		className,
		label,
		hint,
		error,
		secondaryText,
		htmlFor,
		invalid,
		required = false,
		optional = false,
		labelHidden = false,
		...rest
	} = props;

	const generatedId = useId().replace(/:/g, '');
	const childElement = isValidElement(children)
		? (children as React.ReactElement<Record<string, unknown>>)
		: null;
	const childProps = childElement?.props ?? {};
	const controlId = (htmlFor ?? childProps.id ?? `gwp-field-control-${generatedId}`) as string;
	const hintId = hint ? `gwp-field-hint-${generatedId}` : null;
	const errorId = error ? `gwp-field-error-${generatedId}` : null;
	const describedBy = [childProps['aria-describedby'], hintId, errorId].filter(Boolean).join(' ') || undefined;
	const effectiveInvalid = invalid ?? Boolean(error);
	const supportsCustomInvalid = childElement !== null && (typeof childElement.type !== 'string' || 'invalid' in childProps);

	const control =
		childElement !== null
			? cloneElement(childElement, {
					id: controlId,
					'aria-describedby': describedBy,
					'aria-invalid': effectiveInvalid || childProps['aria-invalid'],
					required: required || childProps.required,
					...(effectiveInvalid && supportsCustomInvalid ? { invalid: true } : {}),
				})
			: children;

	const rootClassName = combineClassNames('gwp-field', effectiveInvalid ? 'invalid' : undefined, className);
	const metaContent = secondaryText ?? (optional ? 'Optional' : undefined);

	return (
		<div ref={ref} className={rootClassName} {...rest}>
			{label !== undefined && (
				<div className="gwp-field-header">
					<label
						htmlFor={controlId}
						className={combineClassNames('gwp-field-label', labelHidden ? 'label-hidden' : undefined)}
					>
						<span>{label}</span>
						{required && (
							<span className="gwp-field-required" aria-hidden="true">
								*
							</span>
						)}
					</label>
					{!labelHidden && metaContent !== undefined && <span className="gwp-field-secondary">{metaContent}</span>}
				</div>
			)}

			{control}

			{(hint !== undefined || error !== undefined) && (
				<div className="gwp-field-messages">
					{hint !== undefined && (
						<div id={hintId ?? undefined} className="gwp-field-hint">
							{hint}
						</div>
					)}
					{error !== undefined && (
						<div id={errorId ?? undefined} className="gwp-field-error" role="alert">
							{error}
						</div>
					)}
				</div>
			)}
		</div>
	);
});

GWPField.displayName = 'GWPField';

export default GWPField;
